import React, { useState, useEffect } from 'react';
import { 
  Wifi, 
  Settings as SettingsIcon, 
  Save, 
  TestTube, 
  Play, 
  Square, 
  Trash2, 
  Copy, 
  MessageCircle 
} from 'lucide-react';
import { useSocket } from '../contexts/SocketContext';
import axios from 'axios';
import DashboardHeader from '../components/DashboardHeader';
import DashboardCard from '../components/DashboardCard';
import Input from '../components/Input';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { getBrowserTimezone, getTimezoneOptions, getEffectiveTimezone, getTimezoneInfo } from '../utils/timezone';

const Settings = () => {
  const { socket, isConnected } = useSocket();
  
  // Broker mode: 'local' = Docker broker, 'external' = External broker
  const [brokerMode, setBrokerMode] = useState('local');
  
  const [mqttConfig, setMqttConfig] = useState({
    host: 'mqtt',  // Default to Docker service name
    port: 1883,
    username: '',
    password: '',
    clientId: 'gym-admin',
    // Broker-specific settings
    allowAnonymous: true,
    maxConnections: 100,
    maxMessageSize: 1024,
    websocketPort: 9001
  });
  

  
  const [systemSettings, setSystemSettings] = useState({
    autoRefresh: 30,
    dataRetention: 90,
    lockerExpiryMinutes: 1440,  // Default 24 hours in minutes
    timezone: 'auto'  // Auto-detect browser timezone by default
  });

  // MQTT Listener state
  const [mqttListener, setMqttListener] = useState({
    topic: '#',
    isListening: false,
    messages: [],
    maxMessages: 100
  });

  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testConnectionLoading, setTestConnectionLoading] = useState(false);
  const [testMessageLoading, setTestMessageLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);



  useEffect(() => {
    // Load settings from API
    const loadSettings = async () => {
      try {
        const response = await axios.get('/api/settings');
        const settings = response.data;
        
        if (settings.mqttConfig) {
          setMqttConfig(settings.mqttConfig);
          // Detect broker mode based on host
          if (settings.mqttConfig.host === 'mqtt' || settings.mqttConfig.host === 'localhost') {
            setBrokerMode('local');
          } else {
            setBrokerMode('external');
          }
        }

        if (settings.systemSettings) {
          setSystemSettings(prevSettings => ({
            ...prevSettings,
            ...settings.systemSettings,
            // Convert legacy lockerExpiryHours to lockerExpiryMinutes if needed
            lockerExpiryMinutes: settings.systemSettings.lockerExpiryMinutes || 
                                (settings.systemSettings.lockerExpiryHours ? settings.systemSettings.lockerExpiryHours * 60 : 1440),
            // Default to auto-detect if no timezone is set
            timezone: settings.systemSettings.timezone || 'auto'
          }));
        }
        
        console.log('⚙️ Settings loaded from API:', settings);
      } catch (error) {
        console.error('❌ Failed to load settings from API:', error);
        
        // Fallback to localStorage if API fails
        const savedMqtt = localStorage.getItem('mqttConfig');
        const savedSystem = localStorage.getItem('systemSettings');
        const savedMqttListener = localStorage.getItem('mqttListener');

        if (savedMqtt) setMqttConfig(JSON.parse(savedMqtt));
        if (savedSystem) {
          const parsedSystem = JSON.parse(savedSystem);
          setSystemSettings(prevSettings => ({
            ...prevSettings,
            ...parsedSystem,
            // Convert legacy lockerExpiryHours to lockerExpiryMinutes if needed
            lockerExpiryMinutes: parsedSystem.lockerExpiryMinutes || 
                                (parsedSystem.lockerExpiryHours ? parsedSystem.lockerExpiryHours * 60 : 1440),
            // Default to auto-detect if no timezone is set
            timezone: parsedSystem.timezone || 'auto'
          }));
        }
        if (savedMqttListener) setMqttListener(prev => ({ ...prev, ...JSON.parse(savedMqttListener), messages: [], isListening: false }));
      }
    };

    loadSettings();
  }, []);

  // MQTT message listener
  useEffect(() => {
    if (!socket) {
      console.log('🔌 Socket not available for MQTT listener');
      return;
    }

    console.log('🎧 Setting up MQTT listener...', { 
      isListening: mqttListener.isListening, 
      topic: mqttListener.topic,
      socketConnected: socket.connected 
    });

    const handleMqttMessage = (data) => {
      console.log('📨 Received MQTT message:', data);
      
      // Check if the message topic matches the listener topic pattern
      const topicMatches = (pattern, topic) => {
        if (pattern === '#') return true;
        if (pattern === topic) return true;
        
        // Handle wildcards
        const patternParts = pattern.split('/');
        const topicParts = topic.split('/');
        
        for (let i = 0; i < patternParts.length; i++) {
          if (patternParts[i] === '#') return true;
          if (patternParts[i] === '+') continue;
          if (patternParts[i] !== topicParts[i]) return false;
        }
        
        return patternParts.length === topicParts.length;
      };

      const matches = topicMatches(mqttListener.topic, data.topic);
      console.log('🔍 Topic match check:', { 
        pattern: mqttListener.topic, 
        topic: data.topic, 
        matches 
      });

      if (matches) {
        const newMessage = {
          id: Date.now() + Math.random(),
          timestamp: new Date().toLocaleTimeString(),
          topic: data.topic,
          payload: data.payload,
          rawPayload: JSON.stringify(data.payload, null, 2)
        };

        console.log('✅ Adding message to list:', newMessage);

        // Only add message if currently listening
        setMqttListener(prev => {
          if (!prev.isListening) {
            console.log('🚫 Not listening, ignoring message');
            return prev;
          }
          
          return {
            ...prev,
            messages: [newMessage, ...prev.messages].slice(0, prev.maxMessages)
          };
        });
      }
    };

    const handleTestResponse = (data) => {
      console.log('🧪 Received test response:', data);
      alert(`✅ Socket.IO test successful!\nServer response: ${data.message}\nClient ID: ${data.clientId}`);
    };

    socket.on('mqtt-message', handleMqttMessage);
    socket.on('test-response', handleTestResponse);
    console.log('🔗 MQTT message listener registered');

    return () => {
      console.log('🔌 Cleaning up MQTT message listener');
      socket.off('mqtt-message', handleMqttMessage);
      socket.off('test-response', handleTestResponse);
    };
  }, [socket, mqttListener.topic, mqttListener.maxMessages, mqttListener.isListening]); // Include necessary dependencies

  const handleSave = async () => {
    setLoading(true);
    try {
      // Save to API
      const response = await axios.post('/api/settings', { mqttConfig, systemSettings, mqttListener });
      
      if (response.data.success) {
        console.log('✅ Settings saved to API:', response.data);
        
        // Also save to localStorage as backup
        localStorage.setItem('mqttConfig', JSON.stringify(mqttConfig));
        localStorage.setItem('systemSettings', JSON.stringify(systemSettings));
        localStorage.setItem('mqttListener', JSON.stringify({
          topic: mqttListener.topic,
          maxMessages: mqttListener.maxMessages
        }));
        
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        throw new Error(response.data.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('❌ Error saving settings to API:', error);
      
      // Fallback to localStorage only
      try {
        localStorage.setItem('mqttConfig', JSON.stringify(mqttConfig));
        localStorage.setItem('systemSettings', JSON.stringify(systemSettings));
        localStorage.setItem('mqttListener', JSON.stringify({
          topic: mqttListener.topic,
          maxMessages: mqttListener.maxMessages
        }));
        
        alert('⚠️ Settings saved to local storage only. Server connection failed.');
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } catch (localError) {
        console.error('❌ Failed to save to localStorage:', localError);
        alert('❌ Failed to save settings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const startMqttListener = () => {
    console.log('🚀 Starting MQTT listener...', { 
      topic: mqttListener.topic, 
      maxMessages: mqttListener.maxMessages,
      socketConnected: isConnected 
    });
    setMqttListener(prev => ({ ...prev, isListening: true, messages: [] }));
  };

  const stopMqttListener = () => {
    console.log('🛑 Stopping MQTT listener...');
    setMqttListener(prev => ({ ...prev, isListening: false }));
  };

  const clearMqttMessages = () => {
    console.log('🗑️ Clearing MQTT messages...');
    setMqttListener(prev => ({ ...prev, messages: [] }));
  };

  const copyMessageToClipboard = (message) => {
    const text = `Topic: ${message.topic}\nTimestamp: ${message.timestamp}\nPayload:\n${message.rawPayload}`;
    navigator.clipboard.writeText(text).then(() => {
      // Could show a toast notification here
    });
  };

  const testMqttConnection = async () => {
    setTestConnectionLoading(true);
    setTestResult(null);
    
    try {
      const response = await axios.post('/api/test-mqtt', mqttConfig);
      setTestResult({
        success: true,
        message: response.data.message || 'MQTT connection test successful!'
      });
      setShowTestModal(true);
    } catch (error) {
      let errorMessage = 'MQTT connection test failed. Please check your configuration.';
      
      if (error.response) {
        // Server responded with error status
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 500) {
          errorMessage = 'Connection failed. Please check your broker host, port, and credentials.';
        } else if (error.response.status === 404) {
          errorMessage = 'Test endpoint not found. Please check server configuration.';
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your server connection.';
      } else {
        // Other error
        errorMessage = error.message || errorMessage;
      }
      
      setTestResult({
        success: false,
        message: errorMessage
      });
      setShowTestModal(true);
    } finally {
      setTestConnectionLoading(false);
    }
  };

  const testMqttMessage = async () => {
    setTestMessageLoading(true);
    setTestResult(null);
    
    try {
      const response = await axios.post('/api/test-mqtt-message');
      setTestResult({
        success: true,
        message: response.data.message || 'Test MQTT message sent! Check the message listener.'
      });
      setShowTestModal(true);
    } catch (error) {
      let errorMessage = 'Failed to send test MQTT message. Make sure MQTT is connected.';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = 'MQTT client not connected. Please establish connection first.';
        } else if (error.response.status === 500) {
          errorMessage = 'Failed to publish message. Please check MQTT broker connection.';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your server connection.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      setTestResult({
        success: false,
        message: errorMessage
      });
      setShowTestModal(true);
    } finally {
      setTestMessageLoading(false);
    }
  };

  return (
    <div className="p-6">
      <DashboardHeader title="Settings" subtitle="Configure system preferences and MQTT connection" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* MQTT Broker Configuration */}
        <DashboardCard>
          <div className="flex items-center space-x-3 mb-6">
            <Wifi className="w-6 h-6 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">MQTT Broker Configuration</h3>
          </div>

          <div className="space-y-4">
            {/* Broker Mode Toggle */}
            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-2">
                Broker Mode
              </label>
              <div className="flex rounded-lg overflow-hidden border border-cyan-500/30">
                <button
                  type="button"
                  onClick={() => {
                    setBrokerMode('local');
                    setMqttConfig({ ...mqttConfig, host: 'mqtt', port: 1883 });
                  }}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    brokerMode === 'local'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-slate-700/50 text-cyan-200 hover:bg-slate-600/50'
                  }`}
                >
                  Local Docker Broker
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBrokerMode('external');
                    setMqttConfig({ ...mqttConfig, host: '', port: 1883 });
                  }}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    brokerMode === 'external'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-slate-700/50 text-cyan-200 hover:bg-slate-600/50'
                  }`}
                >
                  External Broker
                </button>
              </div>
              <p className="text-xs text-cyan-200/70 mt-1">
                {brokerMode === 'local' 
                  ? 'Uses the MQTT broker running inside Docker. Locker devices must connect to your machine IP.'
                  : 'Connect to an external MQTT broker (e.g., on the locker device or a remote server).'}
              </p>
            </div>

            {/* Info box for local mode */}
            {brokerMode === 'local' && (
              <div className="bg-cyan-900/30 border border-cyan-500/30 rounded-lg p-3">
                <p className="text-sm text-cyan-200">
                  <strong>Local Broker Active</strong><br />
                  External devices should connect to: <code className="bg-slate-800 px-1 rounded">192.168.68.2:1883</code>
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Broker Host
              </label>
              <Input
                type="text"
                value={mqttConfig.host}
                onChange={(e) => setMqttConfig({ ...mqttConfig, host: e.target.value })}
                placeholder={brokerMode === 'local' ? 'mqtt' : 'e.g., 192.168.68.100'}
                disabled={brokerMode === 'local'}
              />
              {brokerMode === 'local' && (
                <p className="text-xs text-cyan-200/70 mt-1">
                  Using Docker internal hostname. Change to "External Broker" to specify a custom host.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                MQTT Port
              </label>
              <Input
                type="number"
                value={mqttConfig.port}
                onChange={(e) => setMqttConfig({ ...mqttConfig, port: parseInt(e.target.value) })}
                placeholder="1883"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Username (optional)
              </label>
              <Input
                type="text"
                value={mqttConfig.username}
                onChange={(e) => setMqttConfig({ ...mqttConfig, username: e.target.value })}
                placeholder="Leave empty for anonymous"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Password (optional)
              </label>
              <Input
                type="password"
                value={mqttConfig.password}
                onChange={(e) => setMqttConfig({ ...mqttConfig, password: e.target.value })}
                placeholder="Leave empty for anonymous"
              />
            </div>

            {brokerMode === 'local' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-cyan-200 mb-1">
                    WebSocket Port
                  </label>
                  <Input
                    type="number"
                    value={mqttConfig.websocketPort}
                    onChange={(e) => setMqttConfig({ ...mqttConfig, websocketPort: parseInt(e.target.value) })}
                    placeholder="9001"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="allowAnonymous"
                    checked={mqttConfig.allowAnonymous}
                    onChange={(e) => setMqttConfig({ ...mqttConfig, allowAnonymous: e.target.checked })}
                    className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                  <label htmlFor="allowAnonymous" className="text-sm font-medium text-cyan-200">
                    Allow Anonymous Connections
                  </label>
                </div>
              </>
            )}

            <div className="flex space-x-2">
              <Button 
                variant="secondary" 
                className="flex-1 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                onClick={testMqttConnection}
                disabled={testConnectionLoading}
              >
                {testConnectionLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>Testing...</span>
                  </>
                ) : (
                  <>
                    <TestTube className="w-4 h-4" />
                    <span>Test Connection</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </DashboardCard>

        {/* MQTT Listener */}
        <DashboardCard className="lg:col-span-2">
          <div className="flex items-center space-x-3 mb-6">
            <MessageCircle className="w-6 h-6 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">MQTT Message Listener</h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-cyan-200 mb-1">
                  Topic to Monitor
                </label>
                <Input
                  type="text"
                  value={mqttListener.topic}
                  onChange={(e) => setMqttListener(prev => ({ ...prev, topic: e.target.value }))}
                  placeholder="# (all topics)"
                  disabled={mqttListener.isListening}
                />
                <p className="text-xs text-cyan-200 mt-1">
                  Use # for all topics, + for single level wildcard, or specific topic
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-cyan-200 mb-1">
                  Max Messages
                </label>
                <select
                  value={mqttListener.maxMessages}
                  onChange={(e) => setMqttListener(prev => ({ ...prev, maxMessages: parseInt(e.target.value) }))}
                  className="input"
                  disabled={mqttListener.isListening}
                >
                  <option value={50}>50 messages</option>
                  <option value={100}>100 messages</option>
                  <option value={200}>200 messages</option>
                  <option value={500}>500 messages</option>
                </select>
              </div>

              <div className="flex space-x-2">
                {!mqttListener.isListening ? (
                  <Button
                    variant="primary"
                    className="flex items-center space-x-2 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={startMqttListener}
                    disabled={!isConnected}
                  >
                    <Play className="w-4 h-4" />
                    <span>Start Listening</span>
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    className="flex items-center space-x-2 flex-1"
                    onClick={stopMqttListener}
                  >
                    <Square className="w-4 h-4" />
                    <span>Stop Listening</span>
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  onClick={clearMqttMessages}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Clear</span>
                </Button>
              </div>

              <div className="pt-2">
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center space-x-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={testMqttMessage}
                  disabled={testMessageLoading}
                >
                  {testMessageLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending Message...</span>
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4" />
                      <span>Send Test MQTT Message</span>
                    </>
                  )}
                </Button>
              </div>

              {/* Connection Status */}
              <div className="pt-2">
                <div className="flex items-center space-x-2 text-sm">
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className={isConnected ? 'text-green-600' : 'text-red-600'}>
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
              </div>
            </div>

            {/* Message Display */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 border rounded-lg h-96 overflow-hidden">
                <div className="p-3 border-b bg-gray-100">
                  <h4 className="text-sm font-medium text-gray-700">Received Messages</h4>
                </div>
                <div className="h-full overflow-y-auto p-3 space-y-2">
                  {mqttListener.messages.length === 0 ? (
                    <div className="text-center text-gray-500 mt-8">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No messages received yet</p>
                      <p className="text-xs">Start listening to see MQTT messages</p>
                    </div>
                  ) : (
                    mqttListener.messages.map((message) => (
                      <div key={message.id} className="bg-white border rounded p-3 text-sm">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{message.topic}</p>
                            <p className="text-xs text-gray-500">{message.timestamp}</p>
                          </div>
                          <button
                            onClick={() => copyMessageToClipboard(message)}
                            className="ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded p-2 overflow-x-auto">
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                            {message.rawPayload}
                          </pre>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </DashboardCard>



        {/* System Settings */}
        <DashboardCard>
          <div className="flex items-center space-x-3 mb-6">
            <SettingsIcon className="w-6 h-6 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">System Settings</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Auto Refresh Interval (seconds)
              </label>
              <select
                value={systemSettings.autoRefresh}
                onChange={(e) => setSystemSettings({ ...systemSettings, autoRefresh: parseInt(e.target.value) })}
                className="input"
              >
                <option value={15}>15 seconds</option>
                <option value={30}>30 seconds</option>
                <option value={60}>1 minute</option>
                <option value={300}>5 minutes</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Data Retention (days)
              </label>
              <select
                value={systemSettings.dataRetention}
                onChange={(e) => setSystemSettings({ ...systemSettings, dataRetention: parseInt(e.target.value) })}
                className="input"
              >
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Default Locker Assignment Duration (minutes)
              </label>
              <Input
                type="number"
                value={systemSettings.lockerExpiryMinutes}
                onChange={(e) => setSystemSettings({ ...systemSettings, lockerExpiryMinutes: parseInt(e.target.value) })}
                placeholder="1440"
                min="1"
              />
              <p className="text-xs text-cyan-200 mt-1">
                Duration in minutes (e.g., 60 = 1 hour, 1440 = 24 hours)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Timezone
              </label>
              <select
                value={systemSettings.timezone}
                onChange={(e) => setSystemSettings({ ...systemSettings, timezone: e.target.value })}
                className="input"
              >
                {getTimezoneOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-cyan-200 mt-1">
                Active timezone: {getEffectiveTimezone(systemSettings.timezone)} | Current time: {getTimezoneInfo(systemSettings.timezone).currentTime}
              </p>
            </div>


          </div>
        </DashboardCard>


      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? (
            <TestTube className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span>{loading ? 'Saving...' : saved ? 'Saved!' : 'Save Settings'}</span>
        </Button>
      </div>

      {/* Test Result Modal */}
      <Modal 
        open={showTestModal} 
        onClose={() => setShowTestModal(false)}
        className="max-w-md"
      >
        <div className="text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            testResult?.success ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {testResult?.success ? (
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${
            testResult?.success ? 'text-green-800' : 'text-red-800'
          }`}>
            {testResult?.success ? 'Test Successful' : 'Test Failed'}
          </h3>
          <p className="text-gray-600 mb-4">
            {testResult?.message}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Settings; 